import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { useInstallPrompt } from '@core/hooks/useInstallPrompt';
import { cn } from '@lib/utils';

interface InstallAppBannerProps {
  /**
   * Posición del banner
   * @default 'bottom'
   */
  position?: 'top' | 'bottom';
  
  /**
   * Tiempo de espera antes de mostrar el banner (ms)
   * @default 30000 (30 segundos)
   */
  delayMs?: number;
  
  /**
   * Callback cuando el usuario instala la app
   */
  onInstall?: () => void;
  
  /**
   * Callback cuando el usuario descarta el banner
   */
  onDismiss?: () => void;
}

/**
 * Banner que invita a instalar la PWA
 * 
 * Se muestra automáticamente después de un delay si:
 * - La app no está instalada
 * - El prompt está disponible
 * - No fue descartado recientemente
 * - El usuario está en móvil
 * 
 * @example
 * ```tsx
 * <InstallAppBanner 
 *   position="bottom"
 *   delayMs={30000}
 *   onInstall={() => console.log('App installed!')}
 * />
 * ```
 */
export function InstallAppBanner({
  position = 'bottom',
  delayMs = 30000,
  onInstall,
  onDismiss,
}: InstallAppBannerProps) {
  const { canInstall, isInstalled, isDismissed, promptInstall, dismissPrompt } = useInstallPrompt();
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Detectar si estamos en móvil
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Mostrar el banner después del delay
  useEffect(() => {
    if (!canInstall || isInstalled || isDismissed || !isMobile) {
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [canInstall, isInstalled, isDismissed, isMobile, delayMs]);

  const handleInstall = async () => {
    setIsInstalling(true);
    
    try {
      const result = await promptInstall();
      
      if (result.outcome === 'accepted') {
        setIsVisible(false);
        onInstall?.();
      }
    } catch (error) {
      console.error('Error installing app:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed z-50 animate-in',
        position === 'bottom' ? 'bottom-20 sm:bottom-6' : 'top-6',
        'left-4 right-4', // Fixed positioning con margen
        'slide-in-from-bottom' // Animación de entrada
      )}
      role="banner"
      aria-label="Banner de instalación de aplicación"
    >
      <div className="card mx-auto max-w-md overflow-hidden shadow-xl">
        <div className="flex items-center gap-4 p-4">
          {/* Icono de la app */}
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-merino">
            <Smartphone className="h-6 w-6 text-white" />
          </div>

          {/* Contenido */}
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-sm font-semibold text-gray-900">
              Instala Merino Admin
            </h3>
            <p className="mt-0.5 text-xs text-gray-600">
              Acceso rápido desde tu pantalla de inicio
            </p>
          </div>

          {/* Botones */}
          <div className="flex gap-2">
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="btn-primary px-3 py-2 text-sm"
              aria-label="Instalar aplicación"
            >
              {isInstalling ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Instalar</span>
                </>
              )}
            </button>

            <button
              onClick={handleDismiss}
              className="btn-ghost p-2"
              aria-label="Cerrar banner"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Beneficios (opcional, solo en desktop) */}
        <div className="hidden border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-600 sm:block">
          <ul className="flex gap-4">
            <li className="flex items-center gap-1">
              <span className="text-merino-600">✓</span> Funciona offline
            </li>
            <li className="flex items-center gap-1">
              <span className="text-merino-600">✓</span> Acceso rápido
            </li>
            <li className="flex items-center gap-1">
              <span className="text-merino-600">✓</span> Sin usar espacio
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
